import {DateTime} from 'luxon';
import React, {FC, ReactNode, useState} from 'react';
import styled, {css} from 'styled-components';

import {buildHoverParentClassName} from '../../../../libs/shared/core/src/helpers/browser/hoverHelpers';
import {ChromelessButton} from '../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {DateItemWrapperRendererType} from './dateRangeComboBox';

/*
 * Props.
 */

interface DatePickerGridItemProps {
  day: DateTime;
  isDifferentMonth: boolean;
  isSelectable: boolean;
  isSelected: boolean;
  isHighlighted: boolean;
  isHighlightedFromRange: boolean;
  isStartDateHighlightedFromRange: boolean;
  isEndDateHighlightedFromRange: boolean;
  onSelect: () => void;
  datePickerGridItemWrapperRenderer?: DateItemWrapperRendererType;
  disabledIcon?: ReactNode;
  color: string | undefined;
  disabledColor: string | undefined;
  backgroundHoverColor: string | undefined;
  hideDifferentMonth?: boolean;
  isFirstDayInWeek?: boolean;
  isLastDayInWeek?: boolean;
}

/*
 * Style.
 */

interface DayStyleProps {
  $isSelected: boolean;
  $isSelectable: boolean;
  $isHighlighted: boolean;
  $isHighlightedFromRange: boolean;
  $isStartDateHighlightedFromRange: boolean;
  $isEndDateHighlightedFromRange: boolean;
  $isDifferentMonth: boolean;
  $color?: string;
  $disabledColor?: string;
  $backgroundHoverColor?: string;
  $hideDifferentMonth?: boolean;
  $isFirstDayInWeek?: boolean;
  $isLastDayInWeek?: boolean;
}

const StyledDayDiv = styled(ChromelessButton)<DayStyleProps>`
  width: 24px;
  padding: 4px 0;
  border-radius: 6px;
  position: relative;

  text-align: center;
  font-size: ${fontSizes.small};
  line-height: 16px;

  ${addNormalDayStyle};
  ${maybeAddHoverDayStyle};
`;

function addNormalDayStyle(props: DayStyleProps) {
  if (props.$hideDifferentMonth && props.$isDifferentMonth) {
    return css`
      opacity: 0;
      pointer-events: none;
    `;
  }

  // Selected state.
  if (props.$isSelected) {
    return css`
      color: ${(p) => p.theme.invertedText};
      font-weight: ${fontWeights.medium};
      background-color: ${(p) => p.theme.interactiveContainer};

      ${props.$isStartDateHighlightedFromRange &&
      css`
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;

        &:after {
          ${highlightedRangePseudoStyles(props)};
          right: -5px;
        }
      `}

      ${props.$isEndDateHighlightedFromRange &&
      css`
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;

        &:after {
          ${highlightedRangePseudoStyles(props)};
          left: -5px;
        }
      `}
    `;
  }

  // Highlighted range state.
  if (props.$isHighlightedFromRange) {
    return css`
      color: ${(p) => p.theme.greys.shade80};
      background-color: ${(p) => p.theme.palette.blue.shade20};
      border-radius: 0;

      &:before,
      &:after {
        ${highlightedRangePseudoStyles(props)}
        left: -5px;
      }

      &:before {
        ${props.$isFirstDayInWeek && 'display: none;'}
      }

      &:after {
        left: auto;
        right: -5px;
        ${props.$isLastDayInWeek && 'display: none;'}
      }
    `;
  }

  // Greyed-out state.
  if (!props.$isSelectable || props.$isDifferentMonth) {
    return css`
      color: ${(p) => props.$disabledColor || p.theme.greys.shade50};
    `;
  }

  // Highlighted state.
  if (props.$isHighlighted) {
    return css`
      color: ${(p) => p.theme.interactiveText};
      background-color: ${(p) => p.theme.palette.blue.shade20};
    `;
  }

  // Normal state.
  return css`
    color: ${(p) => props.$color || p.theme.greys.shade80};
  `;
}

function maybeAddHoverDayStyle(props: DayStyleProps) {
  // Greyed-out state.
  if (!props.$isSelectable) {
    return '';
  }

  // Selected state.
  if (props.$isSelected) {
    return css`
      &:hover {
        background-color: ${(p) => p.theme.palette.blue.shade50};
      }
    `;
  }

  // Highlighted range state.
  if (props.$isHighlightedFromRange) {
    return css`
      &:hover {
        background-color: ${(p) => p.theme.palette.blue.shade30};
      }
    `;
  }

  // Highlighted state.
  if (props.$isHighlighted) {
    return css`
      &:hover {
        color: ${(p) => p.theme.interactiveText};
        background-color: ${(p) => p.theme.palette.blue.shade30};
      }
    `;
  }

  // Normal state.
  return css`
    &:hover {
      color: ${(p) => props.$color || p.theme.greys.shade80};
      background-color: ${(p) => props.$backgroundHoverColor || p.theme.alphas.gray10};
    }
  `;
}
function highlightedRangePseudoStyles(props: DayStyleProps) {
  return css`
    content: '';
    position: absolute;
    top: 0;
    width: 5px;
    bottom: 0;
    background-color: ${(p) => p.theme.palette.blue.shade20};
  `;
}

/*
 * Component.
 */

export const DatePickerGridItem: FC<DatePickerGridItemProps> = (props) => {
  const {day, isSelectable, onSelect, datePickerGridItemWrapperRenderer, disabledIcon} = props;
  const [isHover, setIsHover] = useState(false);
  const isDisabled = datePickerGridItemWrapperRenderer && !isSelectable;

  const styledDaydiv = (
    <StyledDayDiv
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      $isSelected={props.isSelected}
      $isSelectable={props.isSelectable}
      $isHighlighted={props.isHighlighted}
      $isHighlightedFromRange={props.isHighlightedFromRange}
      $isStartDateHighlightedFromRange={props.isStartDateHighlightedFromRange}
      $isEndDateHighlightedFromRange={props.isEndDateHighlightedFromRange}
      $isDifferentMonth={props.isDifferentMonth}
      $color={props.color}
      $disabledColor={props.disabledColor}
      $backgroundHoverColor={props.backgroundHoverColor}
      $hideDifferentMonth={props.hideDifferentMonth}
      $isFirstDayInWeek={props.isFirstDayInWeek}
      $isLastDayInWeek={props.isLastDayInWeek}
      isDisabled={!isSelectable}
      onClick={onSelect}
      className={isDisabled ? buildHoverParentClassName() : undefined}
    >
      {isDisabled && isHover ? disabledIcon : day.day}
    </StyledDayDiv>
  );
  if (isDisabled) {
    return <>{datePickerGridItemWrapperRenderer(styledDaydiv, !isSelectable)}</>;
  }

  return styledDaydiv;
};
